/**
 * @fileoverview Local-first service for the "Data Repair — Inspection delivery
 *               duplicates" admin page (stage runbook Component D).
 * @module system-web/services/deliveryDuplicateRepair
 *
 * Responsibilities:
 * - Pure Dexie reads over `background_jobs` (deliveryDuplicateScan / Apply /
 *   Revert rows) and `tenants` for the repair table.
 * - POST scan/apply/revert to system-api
 *   `/api/system/data-repair/delivery-duplicates/*`.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDexieRaw, BackgroundJob)
 * - ../apiClient (apiFetch)
 * - ./telemetry (browser telemetry)
 *
 * Architecture:
 * - `background_jobs` is server-authoritative and pull-synced; job progress
 *   written by system-api flows to the page through the normal sync pipeline.
 *   Read functions are liveQuery-safe (pure reads, never write).
 * - No optimistic Dexie writes: the 202 response contains only the job id;
 *   the pending job row arrives via sync.
 *
 * @localFirst
 * Source of Truth: `background_jobs` + `tenants` Dexie tables (pull-synced).
 * Write Flow: POST → server job → synced job rows.
 * Sync Strategy: standard pull for `background_jobs` and `tenants`.
 * Conflict Resolution: server wins (read-only collections).
 *
 * @logging
 * Events: delivery_repair_scan, delivery_repair_apply, delivery_repair_revert
 *         (+ _failed variants).
 * Level: info | error.
 * Correlation: jobId where available.
 */

import { getSystemDexieRaw, type BackgroundJob } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';
import { telemetry } from './telemetry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export const DELIVERY_REPAIR_JOB_TYPES = [
  'deliveryDuplicateScan',
  'deliveryDuplicateApply',
  'deliveryDuplicateRevert',
] as const;

/**
 * @contract DeliveryRepairSummary
 * Purpose: Aggregate counts computed by the scan job.
 * Fields:  reports, records, reportsWithDuplicates, extraRecords,
 *          guardLeftovers, copiesDiffer.
 * Sync:    Carried in the completed scan job's payload.
 */
export interface DeliveryRepairSummary {
  reports: number;
  records: number;
  reportsWithDuplicates: number;
  extraRecords: number;
  guardLeftovers: number;
  copiesDiffer: number;
}

/**
 * @contract CopiesDifferEntry
 * Purpose: One "copies differ" sample row from the scan payload.
 * Fields:  identifiers + differingFields + extrasHaveUserEvents.
 * Sync:    Carried in the scan job payload (capped at 300 on the server).
 */
export interface CopiesDifferEntry {
  generatedDocumentId: string;
  reportNumber?: string;
  caseNumber?: string;
  survivorId: string;
  extraIds: string[];
  differingFields: string[];
  extrasHaveUserEvents: boolean;
}

export interface DeliveryRepairJob extends Omit<BackgroundJob, 'payload'> {
  payload?: {
    summary?: DeliveryRepairSummary;
    copiesDifferSample?: CopiesDifferEntry[];
    copiesDifferTruncated?: boolean;
    scanJobId?: string;
    applyJobId?: string;
    results?: {
      setsRepaired: number;
      recordsRemoved: number;
      referencesRepointed: number;
      autoEventsDeleted: number;
    };
    backupCount?: number;
    restored?: number;
    initiatedBy?: string;
  };
}

export interface RepairTenantRow {
  _id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Dexie reads (liveQuery-safe — pure reads, never write)
// ---------------------------------------------------------------------------

/**
 * @localFirst
 * Source of Truth: `background_jobs` Dexie table (pull-synced).
 * Write Flow: read-only.
 * Sync Strategy: standard pull.
 *
 * All delivery-duplicate repair jobs, newest first.
 */
export async function listDeliveryRepairJobs(): Promise<DeliveryRepairJob[]> {
  const raw = getSystemDexieRaw();
  if (!raw) return [];
  const docs = (await raw.table('background_jobs').toArray()) as DeliveryRepairJob[];
  return docs
    .filter((d) => (DELIVERY_REPAIR_JOB_TYPES as readonly string[]).includes(d.jobType))
    .sort((a, b) => String(b.startedAt ?? '').localeCompare(String(a.startedAt ?? '')));
}

/**
 * @localFirst
 * Source of Truth: `tenants` Dexie table (pull-synced).
 * Write Flow: read-only.
 * Sync Strategy: standard pull.
 *
 * Active departments for the repair picker.
 */
export async function listRepairTenants(): Promise<RepairTenantRow[]> {
  const raw = getSystemDexieRaw();
  if (!raw) return [];
  const docs = (await raw.table('tenants').toArray()) as Array<Record<string, any>>;
  return docs
    .filter((t) => t && t._deleted !== true)
    .map((t) => ({ _id: String(t._id), name: String(t.name ?? t._id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------
// API mutations (POST only — jobs are queued server-side)
// ---------------------------------------------------------------------------

/**
 * @process startDeliveryDuplicateScan
 * Steps: POST tenantIds → 202 { jobs: [{tenantId, jobId}] }.
 * Inputs: tenantIds — department ObjectId hex strings (1..50).
 * Outputs: Server-created job ids.
 * Failure Handling: Throws the apiFetch error; telemetry tracks failure.
 * Idempotency: Each call creates new scan jobs.
 */
export async function startDeliveryDuplicateScan(
  tenantIds: string[],
): Promise<Array<{ tenantId: string; jobId: string }>> {
  try {
    const data = await apiFetch('/api/system/data-repair/delivery-duplicates/scan', {
      method: 'POST',
      body: JSON.stringify({ tenantIds }),
    });
    telemetry.trackEvent('delivery_repair_scan', { count: String(tenantIds.length) });
    return data?.jobs ?? [];
  } catch (err) {
    telemetry.trackEvent('delivery_repair_scan_failed', {});
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'startDeliveryDuplicateScan' });
    throw err;
  }
}

/**
 * @process applyDeliveryDuplicateRepair
 * Steps: POST { scanJobId, confirmTenantName } → 202 { jobId }.
 * Failure Handling: 400/404/409 surface as thrown api errors.
 * Idempotency: Server rejects a second apply for the same scan (409).
 */
export async function applyDeliveryDuplicateRepair(
  scanJobId: string,
  confirmTenantName: string,
): Promise<{ jobId: string }> {
  try {
    const data = await apiFetch('/api/system/data-repair/delivery-duplicates/apply', {
      method: 'POST',
      body: JSON.stringify({ scanJobId, confirmTenantName }),
    });
    telemetry.trackEvent('delivery_repair_apply', { scanJobId });
    return { jobId: data?.jobId };
  } catch (err) {
    telemetry.trackEvent('delivery_repair_apply_failed', { scanJobId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'applyDeliveryDuplicateRepair', scanJobId });
    throw err;
  }
}

/**
 * @process revertDeliveryDuplicateRepair
 * Steps: POST { applyJobId, confirmTenantName } → 202 { jobId }.
 * Failure Handling: 400/404/409 surface as thrown api errors.
 * Idempotency: Server rejects reverting an already-reverted apply (409).
 */
export async function revertDeliveryDuplicateRepair(
  applyJobId: string,
  confirmTenantName: string,
): Promise<{ jobId: string }> {
  try {
    const data = await apiFetch('/api/system/data-repair/delivery-duplicates/revert', {
      method: 'POST',
      body: JSON.stringify({ applyJobId, confirmTenantName }),
    });
    telemetry.trackEvent('delivery_repair_revert', { applyJobId });
    return { jobId: data?.jobId };
  } catch (err) {
    telemetry.trackEvent('delivery_repair_revert_failed', { applyJobId });
    telemetry.trackException(err instanceof Error ? err : new Error(String(err)), { operation: 'revertDeliveryDuplicateRepair', applyJobId });
    throw err;
  }
}
